import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-store';
import { useCallback, useEffect, useMemo } from 'react';

import { useAuth } from '../hooks/use-auth';
import {
  clearOrganization,
  type OrganizationData,
  orgStore,
  pickInitialOrg,
  setOrganization,
  setOrganizations,
  switchOrganization as switchOrganizationAction,
} from '../stores/org-store';

export type { OrganizationData } from '../stores/org-store';

interface OrganizationContextValue {
  organization: OrganizationData | null;
  organizations: OrganizationData[];
  setOrganization: (org: OrganizationData | null) => void;
  switchOrganization: (org: OrganizationData) => void;
}

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth();

  const organizations: OrganizationData[] = useMemo(
    () =>
      (user?.organizations ?? []).map((o) => ({
        pid: o.id,
        name: o.name,
        slug: o.slug,
        seasonRegion: o.seasonRegion,
        setupCompletedAt: o.setupCompletedAt,
        role: o.role,
      })),
    [user?.organizations]
  );

  // Sync organizations list into store
  useEffect(() => {
    setOrganizations(organizations);
  }, [organizations]);

  // Auto-pick org when organizations change
  useEffect(() => {
    if (organizations.length === 0) {
      setOrganization(null);
      return;
    }

    const current = orgStore.state.organization;
    if (current && organizations.some((o) => o.pid === current.pid)) {
      return;
    }

    // Don't auto-pick when super admin is on admin pages (org intentionally cleared)
    if (window.location.pathname.startsWith('/admin')) {
      return;
    }

    // Don't override the store when the URL already pins an org. OrgSyncGuard
    // is responsible for keeping the store in sync with the orgPid segment,
    // including for super admins viewing firms they don't belong to.
    if (current && /^\/[0-9a-f-]{36}\//i.test(window.location.pathname)) {
      return;
    }

    const picked = pickInitialOrg(organizations);
    setOrganization(picked);
  }, [organizations]);

  // Clear on logout
  useEffect(() => {
    if (status !== 'authenticated') {
      clearOrganization();
    }
  }, [status]);

  return <>{children}</>;
}

export function useOrganization(): OrganizationContextValue {
  const queryClient = useQueryClient();
  const organization = useStore(orgStore, (s) => s.organization);
  const organizations = useStore(orgStore, (s) => s.organizations);

  const handleSwitch = useCallback(
    (org: OrganizationData) => switchOrganizationAction(org, queryClient),
    [queryClient]
  );

  return { organization, organizations, setOrganization, switchOrganization: handleSwitch };
}
